import { memo, useContext, useMemo } from "react"
import { useSelector } from "react-redux"
import { Box, useTheme } from "@mui/material"

import { useTranslation } from "react-i18next"
import type { AlertSettingAccordion } from "types/alerts.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import useEntityTelemetry from "helpers/hooks/useEntityTelemetry"
import { isWithinOneDay } from "helpers/utils/dateUtils"
import { getFieldConversions } from "helpers/utils/telemetryFields"
import { formatTelemetryData } from "widgets/plots/helpers"
import AlertsContext from "widgets/alerts/AlertsContext"
import Message from "widgets/common/Message"
import Spinner from "widgets/common/Spinner"
import Plot from "widgets/plots/Plot"
import { getAlertViewAndFields, getQueryParams } from "./helpers"

const PlotMemo = memo(Plot)

interface IAlertChartProps {
  alert: AlertSettingAccordion
}

export function AlertChart({ alert }: IAlertChartProps) {
  const theme = useTheme()
  const orgId = useSelector(selectCurrentOrgId)
  const { t, i18n } = useTranslation()
  const { multipleDateRange } = useContext(AlertsContext)
  const locale = i18n.resolvedLanguage || "en"
  // update only when this accordion date range changes
  const alertDateRange = multipleDateRange[alert.id]
  const dateRange = useMemo(() => alertDateRange, [alertDateRange])
  const formattedDateRange = useMemo(
    () => dateRange && { from_date: dateRange.fromDate, to_date: dateRange.toDate },
    [dateRange],
  )
  const { fields, view } = useMemo(
    () => getAlertViewAndFields(alert, theme, t, locale),
    [alert, theme, t, locale],
  )

  const isRawData = useMemo(
    () => (dateRange ? isWithinOneDay(dateRange) : false),
    [dateRange],
  )

  const params = useMemo(
    () => getQueryParams(alert, fields, formattedDateRange, Number(orgId), isRawData),
    [alert, fields, formattedDateRange, orgId, isRawData],
  )

  const def = useMemo(
    () => ({ view, reqConfig: { aggregate: params.aggregate } }),
    [view, params.aggregate],
  )

  const { data, isLoading } = useEntityTelemetry({
    entity: alert.entity,
    entityId: alert.entity_id,
    params,
  })

  // This path does not go through usePlotProcessor, so the field conversions the entity's own
  // plot relies on are applied here — otherwise a field stored in radians would be drawn raw
  // against a degrees axis.
  const conversions = useMemo(() => getFieldConversions(fields), [fields])
  const formattedData = useMemo(
    () => (data ? formatTelemetryData(data, conversions) : data),
    [data, conversions],
  )

  const fromDateMillis = useMemo(
    () => (dateRange?.fromDate ? new Date(dateRange.fromDate).getTime() : NaN),
    [dateRange?.fromDate],
  )
  const toDateMillis = useMemo(
    () => (dateRange?.toDate ? new Date(dateRange.toDate).getTime() : NaN),
    [dateRange?.toDate],
  )

  const plotStyles = useMemo(
    () => ({
      padding: 0,
      border: "none",
      [theme.breakpoints.up("sm")]: {
        padding: 0,
      },
    }),
    [theme],
  )

  return isLoading || data === undefined ? (
    <Spinner />
  ) : (
    <>
      {data.length ? (
        <Box sx={{ height: "400px" }}>
          <PlotMemo
            data={formattedData ?? data}
            def={def}
            styles={plotStyles}
            fromDate={fromDateMillis}
            toDate={toDateMillis}
            isRawData={isRawData}
          />
        </Box>
      ) : (
        <Message messageKey={"generic.NO_DATA_IN_TIME_WINDOW"} />
      )}
    </>
  )
}

export default AlertChart
